import {
  FluidIconConfig,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
} from '@stencil/core';

import { themeVar } from '../../../utils/themes/theme.variables';
import { Utils } from '../../../utils/utils';

/**
 * @displayName Tab Navigation
 *
 * @excludeTag
 */
@Component({
  tag: 'fluid-tab-navigation',
  styleUrl: 'fluid-tab-navigation.less',
  shadow: false,
})
export class FluidTabNavigation
  implements ComponentInterface, FluidThemeInterface
{
  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidTabNavigationElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  @Prop({ mutable: true }) showArrows: boolean;

  @Prop({ mutable: true }) scrollPosition: number;

  @Prop({ mutable: true }) maxScrollPosition: number;

  // ================================================================ //
  // -- Events

  @Event() leftScroll: EventEmitter;

  @Event() rightScroll: EventEmitter;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  // ================================================================ //
  // -- Local Methods

  scrollLeft() {
    this.leftScroll.emit();
  }

  scrollRight() {
    this.rightScroll.emit();
  }

  // ================================================================ //
  // -- Render (Partials)

  renderArrow(side: string) {
    if (side === 'left' && this.scrollPosition === 0) return;
    if (side === 'right' && this.scrollPosition >= this.maxScrollPosition)
      return;

    const iconConfig =
      side === 'left'
        ? themeVar<FluidIconConfig>(this.theme, 'icons.tabs.nav_left')
        : themeVar<FluidIconConfig>(this.theme, 'icons.tabs.nav_right');

    return (
      <div class={`tabs-nav-container nav-${side}`}>
        <button
          class={`tabs-nav-btn nav-${side}`}
          onClick={() =>
            side === 'left' ? this.scrollLeft() : this.scrollRight()
          }
          tabIndex={-1}>
          <fluid-icon
            config={{
              ...iconConfig,
              theme: this.theme,
              overrideGlobalTheme: true,
            }}
          />
        </button>
      </div>
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this.showArrows) return;

    return [this.renderArrow('left'), this.renderArrow('right')];
  }
}
